import type { RouteRecordRaw } from 'vue-router'
import type { MenuRouteDto } from '@/models/auth'

const UserView = () => import('@/views/system/UserView.vue')
const RoleView = () => import('@/views/system/RoleView.vue')
const ParameterView = () => import('@/views/system/ParameterView.vue')
const DictionaryView = () => import('@/views/system/DictionaryView.vue')
const DictionaryDetailView = () => import('@/views/system/DictionaryDetailView.vue')
const HospitalView = () => import('@/views/system/HospitalView.vue')
const PatientListView = () => import('@/views/patient/PatientListView.vue')
const PatientDetailView = () => import('@/views/patient/PatientDetailView.vue')
const PatientAttachmentView = () => import('@/views/patient/PatientAttachmentView.vue')
const AdmissionFormView = () => import('@/views/patient/AdmissionFormView.vue')
const AngiographView = () => import('@/views/patient/AngiographView.vue')
const IvusListView = () => import('@/views/ivus/IvusListView.vue')
const IvusUploadRecordView = () => import('@/views/ivus/IvusUploadRecordView.vue')
const ClinicListView = () => import('@/views/clinic/ClinicListView.vue')
const ClinicDetailView = () => import('@/views/clinic/ClinicDetailView.vue')
const DataEntryView = () => import('@/views/system/DataEntryView.vue')

export interface AuthorizedRouteDefinition {
  permissionPath: string
  additionalPermissionPaths?: string[]
  route: RouteRecordRaw
}

export const authorizedRouteCatalog: AuthorizedRouteDefinition[] = [
  {
    permissionPath: '/patient',
    route: { path: 'patient', name: 'patient', component: PatientListView, meta: { title: '患者管理' } }
  },
  {
    permissionPath: '/patient',
    route: {
      path: 'patient/addadmission',
      name: 'patient-admission',
      component: AdmissionFormView,
      meta: { title: '入院信息' }
    }
  },
  {
    permissionPath: '/patient',
    additionalPermissionPaths: ['/ivusmanage'],
    route: { path: 'patient/detail', name: 'patient-detail', component: PatientDetailView, meta: { title: '患者详情' } }
  },
  {
    permissionPath: '/patient',
    route: { path: 'patient/graphy', name: 'patient-graphy', component: AngiographView, meta: { title: '造影信息' } }
  },
  {
    permissionPath: '/patient',
    route: {
      path: 'patient/imgdetail',
      name: 'patient-image-detail',
      component: PatientAttachmentView,
      meta: { title: '附件图片' }
    }
  },
  {
    permissionPath: '/ivusmanage',
    route: { path: 'ivusmanage', name: 'ivus', component: IvusListView, meta: { title: 'IVUS 管理' } }
  },
  {
    permissionPath: '/ivusmanage',
    route: {
      path: 'ivusmanage/upload-records',
      name: 'ivus-upload-records',
      component: IvusUploadRecordView,
      meta: { title: '上传记录' }
    }
  },
  {
    permissionPath: '/ivusmanage',
    route: {
      path: 'ivusmanage/patient/detail',
      name: 'ivus-patient-detail',
      redirect: (to) => ({ path: '/patient/detail', query: { patientId: to.query.patientId, active: '3' } })
    }
  },
  {
    permissionPath: '/clinic',
    route: { path: 'clinic', name: 'clinic', component: ClinicListView, meta: { title: '临床项目' } }
  },
  {
    permissionPath: '/clinic',
    route: {
      path: 'clinic/relate-detail',
      name: 'clinic-relate-detail',
      component: ClinicDetailView,
      meta: { title: '临床项目详情' }
    }
  },
  {
    permissionPath: '/system/user',
    route: { path: 'system/user', name: 'system-user', component: UserView, meta: { title: '用户设置' } }
  },
  {
    permissionPath: '/system/role',
    route: { path: 'system/role', name: 'system-role', component: RoleView, meta: { title: '角色设置' } }
  },
  {
    permissionPath: '/system/entry',
    route: { path: 'system/entry', name: 'system-entry', component: DataEntryView, meta: { title: '数据录入' } }
  },
  {
    permissionPath: '/system/parameter',
    route: { path: 'system/parameter', name: 'system-parameter', component: ParameterView, meta: { title: '参数设置' } }
  },
  {
    permissionPath: '/system/dictionary',
    route: {
      path: 'system/dictionary',
      name: 'system-dictionary',
      component: DictionaryView,
      meta: { title: '数据字典' }
    }
  },
  {
    permissionPath: '/system/dictionary',
    route: {
      path: 'system/dictionary/detail',
      name: 'system-dictionary-detail',
      component: DictionaryDetailView,
      meta: { title: '字典明细' }
    }
  },
  {
    permissionPath: '/system/hospital',
    route: { path: 'system/hospital', name: 'system-hospital', component: HospitalView, meta: { title: '医院设置' } }
  }
]

export function normalizeMenuPath(path?: string) {
  if (!path) return ''
  const withoutHash = path.trim().replace(/^#/, '')
  if (!withoutHash || /^https?:\/\//i.test(withoutHash)) return ''
  return `/${withoutHash.replace(/^\/+|\/+$/g, '')}`
}

export function collectMenuPaths(menus: MenuRouteDto[]) {
  const paths = new Set<string>()
  const visit = (items: MenuRouteDto[]) => {
    for (const item of items) {
      const path = normalizeMenuPath(item.path)
      if (path) paths.add(path)
      if (item.children?.length) visit(item.children)
    }
  }
  visit(Array.isArray(menus) ? menus : [])
  return paths
}

export function collectMenuPermissions(menus: MenuRouteDto[]) {
  const permissions = new Set<string>()
  const visit = (items: MenuRouteDto[]) => {
    for (const item of items) {
      for (const permission of item.permissions || []) {
        const normalized = permission.trim()
        if (normalized) permissions.add(normalized)
      }
      if (item.children?.length) visit(item.children)
    }
  }
  visit(Array.isArray(menus) ? menus : [])
  return [...permissions]
}

export function isRouteAuthorized(permissionPath: string, menuPaths: Set<string>) {
  if (menuPaths.has(permissionPath)) return true
  return [...menuPaths].some((path) => path.startsWith(`${permissionPath}/`))
}

export function getAuthorizedRouteDefinitions(menus: MenuRouteDto[]) {
  const menuPaths = collectMenuPaths(menus)
  return authorizedRouteCatalog.filter((definition) =>
    [definition.permissionPath, ...(definition.additionalPermissionPaths || [])].some((path) =>
      isRouteAuthorized(path, menuPaths)
    )
  )
}
